import type { ApiError, LimitExceededDetail } from '@/lib/api';

export type LimitInfo = {
  limit: string;
  current: number;
  max: number;
  upgradeUrl: string;
};

type Listener = (info: LimitInfo) => void;

const listeners = new Set<Listener>();

export function emitUpgradeRequired(info: LimitInfo) {
  for (const listener of listeners) listener(info);
}

export function subscribeUpgradeRequired(listener: Listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function isLimitExceededDetail(payload: unknown): payload is LimitExceededDetail {
  if (typeof payload !== 'object' || payload === null) return false;
  const detail = payload as Partial<LimitExceededDetail>;
  return detail.code === 'limit_exceeded'
    && typeof detail.limit === 'string'
    && Number.isFinite(detail.current)
    && Number.isFinite(detail.max)
    && typeof detail.upgradeUrl === 'string'
    && detail.upgradeUrl.length > 0;
}

/** Returns true if it surfaced an upgrade prompt; callers can then suppress further toasts. */
export function handleApiError(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false;
  const apiErr = err as ApiError;
  if (typeof apiErr.isLimitExceeded !== 'function' || !apiErr.isLimitExceeded()) return false;
  if (!isLimitExceededDetail(apiErr.payload)) return false;
  const detail = apiErr.payload;
  emitUpgradeRequired({
    limit: detail.limit,
    current: detail.current,
    max: detail.max,
    upgradeUrl: detail.upgradeUrl,
  });
  return true;
}
